/* SOLEVIA — core storefront components (header, cart drawer, product form) for layout/solevia.liquid.
   UI widgets (gallery, tabs, accordions, carousels, marquee, stories) live in solevia-ui.js. No dependencies.
   Custom elements set themselves up in connectedCallback and clean up in
   disconnectedCallback, so theme-editor section reloads never duplicate listeners. */
(() => {
  'use strict';
  if (window.Solevia && window.Solevia.__loaded) return;
  const S = (window.Solevia = window.Solevia || {});
  S.__loaded = true;
  const t = (k, fallback) => (S.strings && S.strings[k]) || fallback || '';
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const designMode = () => !!(window.Shopify && window.Shopify.designMode);
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
  const DRAWER_SECTION = 'solevia-cart-drawer';
  S.reduced = reduced; S.designMode = designMode; S.define = define;

  /* ---------- Money (mirrors shop.money_format) ---------- */
  S.formatMoney = (cents) => {
    const fmt = S.moneyFormat || '£{{amount}}';
    const value = Number(cents) || 0;
    const fix = (n, d, ts, ds) => {
      const parts = (n / 100).toFixed(d).split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + ts);
      return parts[1] ? whole + ds + parts[1] : whole;
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => {
      switch (key) {
        case 'amount_no_decimals': return fix(value, 0, ',', '.');
        case 'amount_with_comma_separator': return fix(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fix(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fix(value, 2, "'", '.');
        case 'amount_with_space_separator': return fix(value, 2, ' ', ',');
        default: return fix(value, 2, ',', '.');
      }
    }).replace(/<[^>]*>/g, '');
  };

  /* ---------- Live announcer ---------- */
  S.announce = (msg) => {
    const el = document.querySelector('[data-sv-announcer]');
    if (!el || !msg) return;
    el.textContent = '';
    window.setTimeout(() => { el.textContent = msg; }, 80);
  };

  /* ---------- Scroll lock for modal dialogs ---------- */
  const locks = new Set();
  const lock = (k) => { locks.add(k); document.documentElement.classList.add('sv-lock'); };
  const unlock = (k) => { locks.delete(k); if (!locks.size) document.documentElement.classList.remove('sv-lock'); };
  S.lock = lock; S.unlock = unlock;

  /* ---------- Cart API (Shopify Ajax Cart API + Section Rendering API) ---------- */
  class CartError extends Error {}
  const cartRequest = async (url, init) => {
    let res;
    try {
      res = await fetch(url, Object.assign({ headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } }, init));
    } catch (e) {
      throw new CartError(t('networkError', "We couldn't reach the shop. Check your connection and try again."));
    }
    let data = null;
    try { data = await res.json(); } catch (e) { /* non-JSON response */ }
    if (!res.ok || !data || data.status) {
      const msg = (data && (data.description || data.message)) || t('genericError', 'Something went wrong. Please try again.');
      throw new CartError(typeof msg === 'string' ? msg : t('genericError', 'Something went wrong. Please try again.'));
    }
    return data;
  };
  S.cart = {
    add(formData) {
      formData.append('sections', DRAWER_SECTION);
      formData.append('sections_url', window.location.pathname);
      return cartRequest(S.routes.cartAdd + '.js', { method: 'POST', body: formData });
    },
    change(key, quantity) {
      return cartRequest(S.routes.cartChange + '.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: key, quantity, sections: [DRAWER_SECTION], sections_url: window.location.pathname })
      });
    },
    async refresh() {
      const url = window.location.pathname + '?sections=' + DRAWER_SECTION;
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new CartError(t('genericError'));
      return { sections: await res.json() };
    }
  };
  S.drawer = () => document.querySelector('sv-cart-drawer');
  S.updateCount = (count) => {
    document.querySelectorAll('[data-sv-cart-count]').forEach((el) => { el.textContent = count; });
    document.querySelectorAll('[data-sv-cart-open]').forEach((el) => {
      const tpl = count === 1 ? el.dataset.labelOne : el.dataset.labelOther;
      if (tpl) el.setAttribute('aria-label', tpl.replace('[count]', count));
    });
  };

  /* ---------- Header ---------- */
  class SvHeader extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector('[data-sv-menu-toggle]');
      this.panel = this.btn ? document.getElementById(this.btn.getAttribute('aria-controls')) : null;
      this.onKey = (e) => { if (e.key === 'Escape' && this.isOpen()) { this.toggle(false); this.btn.focus(); } };
      this.onPanel = (e) => { if (e.target.closest('a')) this.toggle(false); };
      this.onDoc = (e) => { if (this.isOpen() && !this.panel.contains(e.target) && !this.btn.contains(e.target)) this.toggle(false); };
      this.onClick = () => this.toggle(!this.isOpen());
      this.mq = window.matchMedia('(min-width: 990px)');
      this.onMq = () => { if (this.mq.matches) this.toggle(false); };
      if (this.btn && this.panel) {
        this.btn.addEventListener('click', this.onClick);
        this.panel.addEventListener('click', this.onPanel);
        document.addEventListener('keydown', this.onKey);
        document.addEventListener('click', this.onDoc);
        this.mq.addEventListener('change', this.onMq);
      }
      this.bar = this.querySelector('.sv-header');
      this.onScroll = () => {
        if (this.raf) return;
        this.raf = window.requestAnimationFrame(() => { this.raf = 0; if (this.bar) this.bar.classList.toggle('is-scrolled', window.scrollY > 24); });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
      if ('ResizeObserver' in window && this.bar) {
        this.ro = new ResizeObserver(() => {
          const bar = this.querySelector('.sv-header__bar');
          if (bar) document.documentElement.style.setProperty('--sv-header-h', bar.offsetHeight + 'px');
        });
        this.ro.observe(this.bar);
      }
    }
    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKey);
      document.removeEventListener('click', this.onDoc);
      window.removeEventListener('scroll', this.onScroll);
      if (this.mq) this.mq.removeEventListener('change', this.onMq);
      if (this.ro) this.ro.disconnect();
    }
    isOpen() { return this.btn && this.btn.getAttribute('aria-expanded') === 'true'; }
    toggle(open) {
      if (!this.btn || !this.panel) return;
      this.btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.panel.hidden = !open;
      const lbl = this.btn.querySelector('[data-sv-menu-label]');
      if (lbl) lbl.textContent = open ? lbl.dataset.close : lbl.dataset.open;
    }
  }

  /* ---------- Cart drawer ---------- */
  class SvCartDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      this.onCancel = (e) => { e.preventDefault(); this.close(); };
      this.onDialogClick = (e) => {
        if (e.target === this.dialog) { this.close(); return; }
        if (e.target.closest('[data-sv-drawer-close]')) { e.preventDefault(); this.close(); return; }
        const step = e.target.closest('[data-sv-line-step]');
        if (step) { e.preventDefault(); this.step(step); return; }
        const rm = e.target.closest('[data-sv-line-remove]');
        if (rm) { e.preventDefault(); this.setQty(rm.closest('[data-sv-line]'), 0); }
      };
      this.onChange = (e) => {
        const input = e.target.closest('[data-sv-line-input]');
        if (!input) return;
        const line = input.closest('[data-sv-line]');
        const v = Math.max(0, parseInt(input.value, 10) || 0);
        this.setQty(line, v);
      };
      // Cleanup runs from our own close() and from the dialog's 'close' event (the browser's own
      // Escape handling), whichever comes first, so the scroll lock and focus are always restored.
      this.onClose = () => this.finishClose();
      this.dialog.addEventListener('cancel', this.onCancel);
      this.dialog.addEventListener('close', this.onClose);
      this.dialog.addEventListener('click', this.onDialogClick);
      this.dialog.addEventListener('change', this.onChange);
    }
    disconnectedCallback() {
      if (!this.dialog) return;
      this.dialog.removeEventListener('cancel', this.onCancel);
      this.dialog.removeEventListener('close', this.onClose);
      this.dialog.removeEventListener('click', this.onDialogClick);
      this.dialog.removeEventListener('change', this.onChange);
      unlock('drawer');
    }
    finishClose() {
      if (this.closedDone) return;
      this.closedDone = true;
      this.dialog.classList.remove('is-open');
      unlock('drawer');
      const o = this.opener;
      this.opener = null;
      const target = (o && o.isConnected && o !== document.body && typeof o.focus === 'function') ? o : document.querySelector('[data-sv-cart-open]');
      if (target) target.focus({ preventScroll: true });
    }
    open(opener) {
      if (!this.dialog || this.dialog.open) return;
      this.closedDone = false;
      this.opener = opener || document.activeElement;
      try { this.dialog.showModal(); } catch (e) { this.dialog.setAttribute('open', ''); }
      lock('drawer');
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => this.dialog.classList.add('is-open')));
      const f = this.dialog.querySelector('[data-sv-drawer-close]');
      if (f) window.setTimeout(() => f.focus({ preventScroll: true }), 40);
    }
    close() {
      if (!this.dialog || !this.dialog.open) return;
      this.dialog.classList.remove('is-open');
      const done = () => { if (this.dialog.open) this.dialog.close(); this.finishClose(); };
      if (reduced()) done(); else window.setTimeout(done, 380);
    }
    showError(msg) {
      const el = this.querySelector('[data-sv-drawer-error]');
      if (!el) return;
      el.textContent = msg || '';
      el.hidden = !msg;
    }
    render(sections) {
      const html = sections && sections[DRAWER_SECTION];
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('[data-sv-drawer-content]');
      const cur = this.querySelector('[data-sv-drawer-content]');
      if (!next || !cur) return;
      const active = document.activeElement;
      const focusKey = active && active.closest && active.closest('[data-sv-line]') ? active.closest('[data-sv-line]').dataset.key : null;
      const focusRole = active && active.dataset ? (active.dataset.svLineStep || (active.hasAttribute('data-sv-line-input') ? 'input' : null)) : null;
      cur.replaceWith(next);
      const count = parseInt(next.dataset.count, 10) || 0;
      S.updateCount(count);
      if (this.dialog && this.dialog.open) {
        let target = null;
        if (focusKey) {
          const line = this.querySelector('[data-sv-line][data-key="' + CSS.escape(focusKey) + '"]');
          if (line) target = focusRole === 'input' ? line.querySelector('[data-sv-line-input]') : line.querySelector('[data-sv-line-step="' + focusRole + '"]');
        }
        if (!target || target.disabled) target = this.querySelector('[data-sv-drawer-close]');
        if (target) target.focus({ preventScroll: true });
      }
      document.dispatchEvent(new CustomEvent('sv:cart:updated', { detail: { count } }));
    }
    step(btn) {
      const line = btn.closest('[data-sv-line]');
      const cur = parseInt(line.dataset.qty, 10) || 0;
      this.setQty(line, Math.max(0, cur + parseInt(btn.dataset.svLineStep, 10)));
    }
    async setQty(line, qty) {
      if (!line || line.classList.contains('is-busy')) return;
      line.classList.add('is-busy');
      this.showError('');
      try {
        const data = await S.cart.change(line.dataset.key, qty);
        this.render(data.sections);
        S.announce(qty === 0 ? t('removed', 'Removed from your cart.') : t('updated', 'Cart updated.'));
        const now = (data.items || []).find((i) => i.key === line.dataset.key);
        if (qty > 0 && now && now.quantity < qty) this.showError(t('qtyLimited', 'Only [qty] of this item could be added.').replace('[qty]', now.quantity));
      } catch (err) {
        line.classList.remove('is-busy');
        this.showError(err.message);
        S.announce(err.message);
        try { this.render((await S.cart.refresh()).sections); } catch (e) { /* keep current view */ }
      }
    }
  }

  /* ---------- Product form: variant picker, quantity, add to cart, sticky bar ---------- */
  class SvProduct extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-sv-product-json]');
      this.form = this.querySelector('form[data-sv-product-form]');
      if (!json || !this.form) return;
      try { this.data = JSON.parse(json.textContent); } catch (e) { return; }
      this.sectionId = this.dataset.sectionId;
      this.select = this.form.querySelector('select[name="id"]');
      this.qty = this.form.querySelector('input[name="quantity"]');
      this.atc = this.form.querySelector('[data-sv-atc]');
      this.err = this.querySelector('[data-sv-form-error]');
      this.sticky = this.querySelector('[data-sv-sticky]');
      this.variant = this.data.variants.find((v) => String(v.id) === String(this.select && this.select.value)) || null;

      this.onOptionChange = (e) => {
        const input = e.target.closest('[data-sv-option]');
        if (!input) return;
        this.choose(parseInt(input.dataset.svOption, 10));
      };
      this.onSelectChange = (e) => {
        if (e.target !== this.select) return;
        this.setVariant(this.data.variants.find((v) => String(v.id) === this.select.value) || null, false);
      };
      this.onClick = (e) => {
        const step = e.target.closest('[data-sv-qty-step]');
        if (step) { e.preventDefault(); this.stepQty(parseInt(step.dataset.svQtyStep, 10)); return; }
        if (e.target.closest('[data-sv-sticky-add]')) { e.preventDefault(); this.submit(); }
      };
      this.onQtyChange = (e) => { if (e.target === this.qty) this.setQty(parseInt(this.qty.value, 10)); };
      this.onSubmit = (e) => { e.preventDefault(); this.submit(); };

      this.addEventListener('change', this.onOptionChange);
      this.addEventListener('change', this.onSelectChange);
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onQtyChange);
      this.form.addEventListener('submit', this.onSubmit);

      // Sticky bar visibility: shown once the buy row has scrolled above the viewport.
      const anchor = this.querySelector('[data-sv-buy-anchor]');
      if (this.sticky && anchor && 'IntersectionObserver' in window) {
        this.io = new IntersectionObserver(([en]) => {
          const show = !en.isIntersecting && en.boundingClientRect.top < 0;
          this.sticky.classList.toggle('is-visible', show);
          this.sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
          this.sticky.inert = !show;
          const visibleNow = show && window.getComputedStyle(this.sticky).display !== 'none';
          document.documentElement.classList.toggle('sv-sticky-on', visibleNow);
        });
        this.io.observe(anchor);
      }
      this.render(false);
    }
    disconnectedCallback() {
      if (!this.form) return;
      this.form.removeEventListener('submit', this.onSubmit);
      if (this.io) this.io.disconnect();
      document.documentElement.classList.remove('sv-sticky-on');
    }
    selections() {
      return this.data.options.map((_, i) => {
        const c = this.querySelector('[data-sv-option="' + i + '"]:checked');
        return c ? c.value : null;
      });
    }
    choose(changed) {
      const sel = this.selections();
      const vs = this.data.variants;
      let match = vs.find((v) => v.options.every((o, i) => o === sel[i]));
      if (!match) {
        // Keep everything up to the option just changed, then keep as many later picks as possible.
        const pool = vs.filter((v) => v.options.slice(0, changed + 1).every((o, i) => o === sel[i]));
        const score = (v) => v.options.reduce((n, o, i) => n + (i > changed && o === sel[i] ? 1 : 0), 0) + (v.available ? 0.5 : 0);
        match = pool.sort((a, b) => score(b) - score(a))[0] || null;
      }
      this.setVariant(match, true);
    }
    setVariant(v, fromOptions) {
      this.variant = v;
      if (v && this.select) this.select.value = String(v.id);
      if (v && this.dataset.updateUrl === 'true' && window.history.replaceState) {
        const u = new URL(window.location.href);
        u.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', u.toString());
      }
      this.render(true);
      if (v && v.featured_media && fromOptions !== null) {
        document.dispatchEvent(new CustomEvent('sv:media:select', { detail: { sectionId: this.sectionId, mediaId: v.featured_media } }));
      }
      this.showError('');
    }
    syncOptions() {
      const v = this.variant;
      const vs = this.data.variants;
      const sel = v ? v.options : this.selections();
      this.data.options.forEach((name, i) => {
        const inputs = this.querySelectorAll('[data-sv-option="' + i + '"]');
        let shown = 0;
        inputs.forEach((input) => {
          if (v) input.checked = input.value === v.options[i];
          const prior = (x) => x.options.slice(0, i).every((o, j) => o === sel[j]);
          const exists = i === 0 || vs.some((x) => x.options[i] === input.value && prior(x));
          const avail = vs.some((x) => x.options[i] === input.value && x.available && (i === 0 || prior(x)));
          const wrap = input.closest('[data-sv-option-wrap]');
          if (wrap) {
            wrap.hidden = !exists;
            wrap.classList.toggle('is-unavailable', !avail);
            const so = wrap.querySelector('[data-sv-soldout]');
            if (so) so.hidden = avail;
          }
          if (exists) shown += 1;
        });
        const group = this.querySelector('[data-sv-option-group="' + i + '"]');
        if (group && i > 0) group.hidden = shown <= 1;
        const label = this.querySelector('[data-sv-option-value="' + i + '"]');
        if (label) label.textContent = v ? v.options[i] : '';
      });
    }
    rule() { return (this.variant && this.variant.quantity_rule) || { min: 1, max: null, increment: 1 }; }
    setQty(n) {
      const r = this.rule();
      let q = isNaN(n) ? r.min : n;
      q = Math.max(r.min || 1, q);
      if (r.max) q = Math.min(r.max, q);
      const inc = r.increment || 1;
      q = (r.min || 1) + Math.round((q - (r.min || 1)) / inc) * inc;
      if (this.qty) this.qty.value = q;
      this.render(false);
    }
    stepQty(dir) {
      const r = this.rule();
      const cur = parseInt(this.qty && this.qty.value, 10) || r.min || 1;
      this.setQty(cur + dir * (r.increment || 1));
    }
    render() {
      const v = this.variant;
      this.syncOptions();
      const r = this.rule();
      const q = Math.max(1, parseInt(this.qty && this.qty.value, 10) || 1);
      if (this.qty) {
        this.qty.min = r.min || 1;
        this.qty.step = r.increment || 1;
        if (r.max) this.qty.max = r.max; else this.qty.removeAttribute('max');
      }
      this.querySelectorAll('[data-sv-qty-step="-1"]').forEach((b) => { b.disabled = q <= (r.min || 1); });
      this.querySelectorAll('[data-sv-qty-step="1"]').forEach((b) => { b.disabled = !!(r.max && q >= r.max); });
      this.querySelectorAll('[data-sv-qty-mirror]').forEach((el) => { if ('value' in el && el.tagName === 'INPUT') el.value = q; else el.textContent = q; });

      const now = this.querySelector('[data-sv-price-now]');
      const was = this.querySelector('[data-sv-price-was]');
      const badge = this.querySelector('[data-sv-price-badge]');
      if (v) {
        if (now) now.textContent = S.formatMoney(v.price);
        const sale = v.compare_at_price && v.compare_at_price > v.price;
        if (was) { const wrap = was.closest('[data-sv-was-wrap]') || was; wrap.hidden = !sale; was.textContent = sale ? S.formatMoney(v.compare_at_price) : ''; }
        if (badge) {
          const tpl = badge.dataset.template || '';
          badge.textContent = sale && tpl ? tpl.replace('[amount]', S.formatMoney(v.compare_at_price - v.price)) : '';
        }
      }
      let label; let disabled = false;
      if (!v) { label = t('unavailable', 'Unavailable'); disabled = true; }
      else if (!v.available) { label = t('soldOut', 'Sold out'); disabled = true; }
      else {
        const showTotal = this.atc && this.atc.dataset.showTotal === 'true';
        label = (this.atc ? this.atc.dataset.label : t('addToCart', 'Add to cart')) + (showTotal ? ' — ' + S.formatMoney(v.price * q) : '');
      }
      if (this.atc && !this.busy) {
        this.atc.disabled = disabled;
        const l = this.atc.querySelector('[data-sv-atc-label]');
        if (l) l.textContent = label;
      }
      const stickyBtn = this.querySelector('[data-sv-sticky-add]');
      if (stickyBtn && !this.busy) {
        stickyBtn.disabled = disabled;
        (stickyBtn.querySelector('[data-sv-sticky-label]') || stickyBtn).textContent = disabled ? label : (stickyBtn.dataset.label || t('addToCart', 'Add to cart'));
      }
      const sub = this.querySelector('[data-sv-sticky-sub]');
      if (sub) {
        const parts = [];
        if (v && v.title && v.title !== 'Default Title') parts.push(v.title);
        if (q > 1) parts.push('× ' + q);
        if (v) parts.push(S.formatMoney(v.price * q));
        sub.textContent = parts.join(' · ');
      }
    }
    showError(msg) {
      if (!this.err) return;
      this.err.textContent = msg || '';
      this.err.hidden = !msg;
    }
    setBusy(on) {
      this.busy = on;
      const btns = [this.atc, this.querySelector('[data-sv-sticky-add]')].filter(Boolean);
      btns.forEach((b) => { b.setAttribute('aria-busy', on ? 'true' : 'false'); if (on) b.disabled = true; });
      const l = this.atc && this.atc.querySelector('[data-sv-atc-label]');
      const sp = this.atc && this.atc.querySelector('[data-sv-atc-spinner]');
      if (sp) sp.hidden = !on;
      if (on && l) l.textContent = t('adding', 'Adding…');
      if (!on) this.render();
    }
    async submit() {
      if (this.busy) return;
      if (!this.variant) { this.showError(t('unavailable', 'This combination is unavailable.')); return; }
      if (!this.variant.available) { this.showError(t('soldOut', 'Sold out')); return; }
      this.setBusy(true);
      this.showError('');
      try {
        const fd = new FormData(this.form);
        fd.set('id', String(this.variant.id));
        const data = await S.cart.add(fd);
        const drawer = S.drawer();
        if (drawer) { drawer.render(data.sections); drawer.open(this.atc); }
        else { window.location.href = S.routes.cart; return; }
        S.announce(t('added', 'Added to your cart.'));
      } catch (err) {
        this.showError(err.message);
        S.announce(err.message);
      } finally {
        this.setBusy(false);
      }
    }
  }

  define('sv-header', SvHeader);
  define('sv-cart-drawer', SvCartDrawer);
  define('sv-product', SvProduct);

  /* ---------- Document-level delegation (registered once) ---------- */
  document.addEventListener('click', (e) => {
    const cartOpen = e.target.closest('[data-sv-cart-open]');
    if (cartOpen) {
      const d = S.drawer();
      if (d && d.dialog) { e.preventDefault(); d.open(cartOpen); }
      return;
    }
    const accLink = e.target.closest('[data-sv-open-acc]');
    if (accLink) {
      const id = accLink.dataset.svOpenAcc;
      const panel = document.getElementById(id);
      const acc = panel && panel.closest('sv-accordion');
      if (acc) {
        e.preventDefault();
        const btn = acc.openById(id);
        const item = btn && btn.closest('[data-sv-acc-item]');
        if (item) item.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' });
        if (btn) window.setTimeout(() => btn.focus({ preventScroll: true }), reduced() ? 0 : 500);
      }
      return;
    }
    const accTitle = e.target.closest('[data-sv-open-acc-title]');
    if (accTitle) {
      const want = accTitle.dataset.svOpenAccTitle.trim().toLowerCase();
      const btn = Array.from(document.querySelectorAll('sv-accordion [data-sv-acc-btn]')).find((b) => b.textContent.trim().toLowerCase() === want);
      if (btn) {
        e.preventDefault();
        const acc = btn.closest('sv-accordion');
        acc.toggle(btn, true);
        const item = btn.closest('[data-sv-acc-item]') || btn;
        item.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' });
        window.setTimeout(() => btn.focus({ preventScroll: true }), reduced() ? 0 : 500);
      }
      return;
    }
    const focusAtc = e.target.closest('[data-sv-focus-atc]');
    if (focusAtc) {
      const atc = document.querySelector('[data-sv-atc]');
      if (atc) window.setTimeout(() => atc.focus({ preventScroll: true }), reduced() ? 0 : 700);
      return;
    }
    const dlgOpen = e.target.closest('[data-sv-dialog-open]');
    if (dlgOpen) {
      const dlg = document.getElementById(dlgOpen.dataset.svDialogOpen);
      if (dlg && typeof dlg.showModal === 'function') {
        e.preventDefault();
        dlg._opener = dlgOpen;
        dlg.showModal();
        lock(dlg.id);
        if (!dlg._svBound) {
          dlg._svBound = true;
          const finish = () => { if (!locks.has(dlg.id)) return; unlock(dlg.id); if (dlg._opener && dlg._opener.isConnected) dlg._opener.focus({ preventScroll: true }); };
          dlg.addEventListener('close', finish);
          dlg.addEventListener('click', (ev) => { if (ev.target === dlg || ev.target.closest('[data-sv-dialog-close]')) { dlg.close(); finish(); } });
        }
      }
    }
  });

  // Newsletter: validate client-side, then let Shopify's customer form submit normally.
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-sv-newsletter]');
    if (!form) return;
    const input = form.querySelector('input[type="email"]');
    const msg = form.querySelector('[data-sv-news-msg]');
    const ok = input && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(input.value.trim());
    if (!ok) {
      e.preventDefault();
      input.setAttribute('aria-invalid', 'true');
      if (msg) { msg.textContent = msg.dataset.invalid; msg.className = 'sv-news__msg sv-news__msg--err'; }
      input.focus();
      return;
    }
    const btn = form.querySelector('[type="submit"]');
    if (btn) { if (btn.dataset.busy === 'true') { e.preventDefault(); return; } btn.dataset.busy = 'true'; btn.setAttribute('aria-busy', 'true'); }
  });
  document.addEventListener('input', (e) => {
    if (e.target.matches && e.target.matches('[data-sv-newsletter] input[type="email"]')) e.target.removeAttribute('aria-invalid');
  });

  /* ---------- Reveal on scroll (content is visible without JS) ---------- */
  let io = null;
  const reveal = (root) => {
    if (reduced() || designMode() || !('IntersectionObserver' in window)) return;
    if (!io) {
      io = new IntersectionObserver((entries) => entries.forEach((en) => {
        if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); }
      }), { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    }
    const vh = window.innerHeight || 800;
    const els = Array.from((root || document).querySelectorAll('[data-sv-reveal]')).filter((el) => !el.classList.contains('sv-reveal-ready') && el.getBoundingClientRect().top > vh);
    els.forEach((el) => { el.classList.add('sv-reveal-ready'); io.observe(el); });
    window.setTimeout(() => els.forEach((el) => el.classList.add('is-in')), 6000);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => reveal(document)); else reveal(document);

  /* ---------- Theme editor support ---------- */
  document.addEventListener('shopify:section:load', (e) => reveal(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    const el = e.target;
    if (!el || !el.closest) return;
    const tab = el.matches('[role="tab"]') ? el : null;
    if (tab) { const tabs = tab.closest('sv-tabs'); if (tabs) tabs.select(tab); return; }
    const story = el.closest('[data-sv-story]');
    if (story) { const st = story.closest('sv-stories'); if (st) st.go(st.items.indexOf(story)); return; }
    const acc = el.closest('[data-sv-acc-item]');
    if (acc) { const b = acc.querySelector('[data-sv-acc-btn]'); const a = acc.closest('sv-accordion'); if (a && b) a.toggle(b, true); return; }
    const car = el.closest('sv-carousel');
    if (car && car.reveal) car.reveal(el);
  });
  document.addEventListener('shopify:section:select', (e) => {
    const d = e.target.querySelector && e.target.querySelector('sv-cart-drawer');
    if (d) d.open();
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    const d = e.target.querySelector && e.target.querySelector('sv-cart-drawer');
    if (d) d.close();
  });
})();
